/**
 * CheckEmailScreen — shown after a successful registration.
 *
 * Reads `email` from search params and provides a resend button so the
 * user can request another confirmation link without leaving the screen.
 */

import React, { useState } from 'react'
import { View, Text, KeyboardAvoidingView, Platform } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { Button } from '../../components/ui/Button'
import { useToast } from '../../components/ui/Toast'
import { resendConfirmation } from '../../lib/api'

export default function CheckEmailScreen() {
  const { email } = useLocalSearchParams<{ email?: string }>()
  const { showToast } = useToast()
  const router = useRouter()
  const [loading, setLoading] = useState(false)

  async function handleResend() {
    if (!email) return
    setLoading(true)
    try {
      await resendConfirmation(email)
      showToast('Confirmation email sent', 'success')
    } catch {
      showToast('Could not resend — please try again', 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      className="flex-1 bg-canvas"
    >
      <View className="flex-1 justify-center px-6 gap-8">
        <View className="gap-3">
          <Text className="text-4xl font-stencil text-ink uppercase">Check your inbox</Text>
          <Text className="font-typed text-sm text-ink-soft">
            We sent a confirmation link to{' '}
            {email ? <Text className="text-ink">{email}</Text> : 'your email address'}.
            {'\n\n'}
            Follow the link to activate your account, then come back here to sign in.
          </Text>
        </View>

        <View className="gap-3">
          {/* Primary CTA — once they've confirmed via the email link, this is
              how they get back into the app. A real button (not the old faint
              text link) so it's an obvious tap target. */}
          <Button
            title="Sign In"
            size="lg"
            onPress={() => router.replace('/(auth)/login')}
          />
          {email ? (
            <Button
              title="Resend email"
              variant="secondary"
              onPress={handleResend}
              loading={loading}
            />
          ) : null}
        </View>
      </View>
    </KeyboardAvoidingView>
  )
}
